import { debounce } from 'lodash';
import { parse } from 'query-string';
import React, { useCallback, useEffect, useState } from 'react';
import { Route, Switch, useHistory, useLocation } from 'react-router';
import { useToasts } from 'react-toast-notifications';
import { v4 as uuid } from 'uuid';
import CustomerPortalComponent from '../../../common/CustomerPortalComponent';
import CopyToClipboardLink from '../../../components/CopyToClipboardLink';
import Header from '../../../components/Header';
import Loader from '../../../components/Loader';
import { Pill } from '../../../components/Pill';
import SingleColGrid from '../../../components/SingleColGrid';
import { ROUTES } from '../../../routes/routes';
import {
    CANCELLATION_MODE,
    CANCELLATION_PRORATION_BEHAVIOUR,
    CANCELLATION_REASON_COLLECTION_MODE,
    CustomerPortal,
    CustomerPortalButtonSize,
    CustomerPortalLayout,
    SUBSCRIPTION_UPDATE_PRORATION,
} from '../../../types/customerPortal';
import { PricingPage } from '../../../types/pricingPage';
import { DEFAULT_PORTAL_CUSTOM_CSS } from '../../../utils/constants';
import { useAxios } from '../../../utils/hooks';
import { withPageIdParam } from '../../PricingPages/NewPage/utils';
import ChoosePricing from './ChoosePricing';
import Configure from './Configure';
import CopySnippet from './CopySnippet';

export default function NewCustomerPortal() {
    const { search } = useLocation();
    const history = useHistory();
    const { addToast } = useToasts();
    const axios = useAxios();

    const [customerPortal, setCustomerPortal] = useState<CustomerPortal>();
    const [pages, setPages] = useState<PricingPage[]>();

    useEffect(() => {
        if (!axios.current) {
            return;
        }

        axios.current
            .get<PricingPage[]>('/pricing-pages')
            .then((res) => setPages(res.data))
            .catch((err) =>
                addToast('Something went wrong. Please reload the page and try again.', { appearance: 'error' })
            );
        const { customerPortalId } = parse(search);
        if (customerPortalId) {
            axios.current
                ?.get<CustomerPortal>(`/customer-portals/${customerPortalId}`)
                .then((res) => {
                    setCustomerPortal(res.data);
                })
                .catch((err) =>
                    addToast('Something went wrong. Please reload the page and try again', { appearance: 'error' })
                );
        }
    }, [axios, addToast, search]);

    const updateCustomerPortal = async (newConfiguration: CustomerPortal) => {
        setCustomerPortal(newConfiguration);
        sendUpdateRequest(newConfiguration);
    };

    const sendUpdateRequest = useCallback(
        debounce(
            async (newConfiguration: CustomerPortal) =>
                axios.current
                    ?.post<CustomerPortal>('/customer-portals', newConfiguration)
                    .catch((err) =>
                        addToast('Something went wrong. Please reload the page and try again.', { appearance: 'error' })
                    ),
            1000
        ),
        [axios, customerPortal, addToast]
    );

    const handleChangePricingPage = (page: PricingPage) => {
        if (customerPortal) {
            updateCustomerPortal({ ...customerPortal, pricingPageId: page.id });
        } else {
            updateCustomerPortal({
                id: uuid(),
                pricingPageId: page.id,
                business_profile: { privacy_policy_url: '', terms_of_service_url: '' },
                features: {
                    invoice_history: { enabled: true },
                    payment_method_update: { enabled: true },
                    subscription_cancel: {
                        enabled: true,
                        mode: CANCELLATION_MODE.PERIOD_END,
                        proration_behavior: CANCELLATION_PRORATION_BEHAVIOUR.NONE,
                        cancellation_reason: {
                            enabled: false,
                            options: [],
                            mode: CANCELLATION_REASON_COLLECTION_MODE.BEFORE,
                            refresh: false,
                        },
                    },
                    subscription_update: {
                        enabled: true,
                        proration_behavior: SUBSCRIPTION_UPDATE_PRORATION.NONE,
                        default_allowed_updates: { price: true, promotion_code: false, quantity: false },
                        disabled_products: [],
                    },
                    customer_update: { enabled: false },
                    subscription_pause: { enabled: false },
                },
                designSettings: {
                    buttonText: 'Manage Subscription',
                    buttonColour: '#1a304e',
                    buttonSize: CustomerPortalButtonSize.MEDIUM,
                    layout: CustomerPortalLayout.HORIZONTAL,
                    placeholderText: 'your@mail.com',
                    customCSS: DEFAULT_PORTAL_CUSTOM_CSS,
                    messages: { checkEmail: 'Please check your inbox', invalidEmail: 'Please check your email' },
                    customWording: { cancelSubscription: '' },
                },
                secureMode: false,
                emailAuth: false,
            });
        }
    };

    const handleNavigateToPricingPage = () => {
        if (customerPortal) {
            history.push(withPageIdParam(ROUTES.PAGES_NEW, customerPortal.pricingPageId));
        }
    };

    const ChoosePricingPage = () => {
        if (!pages) {
            return (
                <div className="col-span-2">
                    <Loader full />
                </div>
            );
        }
        return <ChoosePricing customerPortal={customerPortal} onChangePricing={handleChangePricingPage} />;
    };

    const SubscriptionManagerConfigPage = () => {
        if (!customerPortal) {
            return (
                <div className="col-span-2">
                    <Loader full />
                </div>
            );
        }

        return (
            <Configure
                customerPortal={customerPortal}
                page={pages?.find((page) => page.id === customerPortal.pricingPageId)}
                onUpdate={updateCustomerPortal}
            />
        );
    };

    const SubscriptionManagerSnippetPage = () => {
        if (!customerPortal) {
            return (
                <div className="col-span-2">
                    <Loader full />
                </div>
            );
        }

        return <CopySnippet customerPortal={customerPortal} onUpdate={updateCustomerPortal} />;
    };

    return (
        <div className="flex flex-col w-full">
            {customerPortal && (
                <div className="flex flex-col w-full border-b p-8">
                    <div className="flex">
                        <Header>Preview</Header>
                        {!!customerPortal && (
                            <div className="ml-auto">
                                <button className="link text-sm" onClick={handleNavigateToPricingPage}>
                                    Pricing Page{' '}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 1792 1792"
                                        className="w-4 h-4 inline fill-current"
                                    >
                                        <path d="M1664 960q-152-236-381-353 61 104 61 225 0 185-131.5 316.5T896 1280t-316.5-131.5T448 832q0-121 61-225-229 117-381 353 133 205 333.5 326.5T896 1408t434.5-121.5T1664 960zM944 576q0-20-14-34t-34-14q-125 0-214.5 89.5T592 832q0 20 14 34t34 14 34-14 14-34q0-86 61-147t147-61q20 0 34-14t14-34zm848 384q0 34-20 69-140 230-376.5 368.5T896 1536t-499.5-139T20 1029Q0 994 0 960t20-69q140-229 376.5-368T896 384t499.5 139T1772 891q20 35 20 69z" />
                                    </svg>
                                </button>
                                {' | '}
                                <CopyToClipboardLink name="Portal ID" value={customerPortal.id} />
                            </div>
                        )}
                    </div>
                    <div className="flex pt-4">
                        <div className="flex place-content-center flex-grow">
                            <CustomerPortalComponent customerPortal={customerPortal} disabled poweredBy={false} />
                        </div>
                        <div className="mr-0 ml-auto">
                            {customerPortal.mode && (
                                <Pill
                                    title={`${customerPortal.mode} mode`}
                                    colour={customerPortal.mode === 'live' ? 'GREEN' : 'YELLOW'}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
            <SingleColGrid className="flex-1">
                <Switch>
                    <Route exact path={ROUTES.CUSTOMER_PORTALS_NEW} render={ChoosePricingPage} />
                    <Route exact path={ROUTES.CUSTOMER_PORTALS_CONFIG} render={SubscriptionManagerConfigPage} />
                    <Route exact path={ROUTES.CUSTOMER_PORTALS_SNIPPET} render={SubscriptionManagerSnippetPage} />
                </Switch>
            </SingleColGrid>
        </div>
    );
}
